import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type PatientInterface from '@/domains/patient/typescript/PatientInterface';

export default defineStore('patient', () => {
    const patients = ref<PatientInterface[]>([]);
    const selectedPatient = ref<PatientInterface>();
    const hasPatientSelected = computed<boolean>(() => selectedPatient.value !== undefined);
    // The API only schedules a deletion (202): the patient stays active until the worker deactivates it.
    const deletionRequestedPatientIds = ref(new Set<PatientInterface['id']>());

    const isDeletionPending = (patient: PatientInterface): boolean =>
        !patient.isActive || deletionRequestedPatientIds.value.has(patient.id);

    const findSelectablePatient = (patientId?: string): PatientInterface | undefined => {
        const patientFound = patients.value.find((patient) => patient.id === patientId);
        return patientFound && !isDeletionPending(patientFound) ? patientFound : undefined;
    };

    const selectPatientById = (patientId: string): void => {
        selectedPatient.value = findSelectablePatient(patientId);
    };

    const isPatientSelected = (patient: PatientInterface): boolean => selectedPatient.value?.id === patient.id;

    const unselectPatient = (): void => {
        selectedPatient.value = undefined;
    };

    // The store only holds the current page: a selected patient missing from it stays selected.
    const syncSelectedPatient = (): void => {
        const isSelectedPatientReloaded = patients.value.some((patient) => patient.id === selectedPatient.value?.id);
        if (isSelectedPatientReloaded) {
            selectedPatient.value = findSelectablePatient(selectedPatient.value?.id);
        }
    };

    const markDeletionRequested = (patientId: PatientInterface['id']): void => {
        deletionRequestedPatientIds.value.add(patientId);
        if (selectedPatient.value?.id === patientId) {
            unselectPatient();
        }
    };

    function $reset() {
        console.info('reset patient store');
        patients.value = [];
        selectedPatient.value = undefined;
        deletionRequestedPatientIds.value.clear();
    }

    return {
        patients,
        selectedPatient,
        hasPatientSelected,
        selectPatientById,
        isPatientSelected,
        unselectPatient,
        syncSelectedPatient,
        isDeletionPending,
        markDeletionRequested,

        $reset,
    };
});
